import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState, type FormEvent } from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../core/contexts/AuthContext';
import { extractErrorMessage } from '../../core/services/api';
import { GoogleSignInButton } from '../../shared/components/GoogleSignInButton';

export function RegistrationPage() {
  const { isAuthenticated, register, loginWithGoogle, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (isAuthenticated) return <Navigate to="/" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await register({ email, password, name: name || undefined, tenantName });
      navigate('/', { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const onGoogleCredential = async (idToken: string) => {
    setError(null);
    try {
      const signup = await loginWithGoogle(idToken);
      if (signup) navigate('/complete-signup', { state: signup, replace: true });
      else navigate('/', { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ p: 4 }}>
          <Stack spacing={1} sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h5" color="primary" fontWeight={700}>
              Create an account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You'll get a fresh tenant to work in.
            </Typography>
          </Stack>

          <form onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="At least 8 characters."
                autoComplete="new-password"
              />
              <TextField
                label="Your name"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
              <TextField
                label="Tenant name"
                fullWidth
                required
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                helperText="Displayed in the tenant switcher."
              />
              <Button type="submit" variant="contained" size="large" disabled={loading}>
                {loading ? 'Creating account…' : 'Create account'}
              </Button>
              <GoogleSignInButton
                onCredential={onGoogleCredential}
                text="signup_with"
                disabled={loading}
              />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Already have an account?{' '}
                <Link component={RouterLink} to="/login">
                  Sign in
                </Link>
              </Typography>
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
